import { DocListEntry } from 'src/app/shared/doc-list-entry';

/**
 * Types for the admin "hidden documents" inspector
 * (`GET /AdminHiddenDocuments/logins`, `/documents` in webyto_api-express4-TS-20250331;
 * spec: docs/PROMPT_admin-hidden-documents-api.md). The API never returns a
 * credential column; do not bind to one if it ever appears.
 */

/** One row of the login picker. */
export interface HiddenDocsLogin {
  login: string;
  ladder: number;
  persId: number | null;
  personName: string | null;
  description: string | null;
  /** `false` when the login does not look like a Keycloak `sub` UUID, so the server-side fence can never resolve it. */
  resolvableByFence: boolean;
  /** `true` when labelled as a newcomer but sitting at group >= 10000. */
  newcomerLabelled: boolean;
}

export interface HiddenDocsTarget {
  login: string | null;
  ladder: number | null;
  source: 'login' | 'override';
  resolvable: boolean;
}

export interface HiddenDocsSummary {
  totalInWindow: number;
  hidden: number;
  /** Reason code -> count. Codes the frontend does not know are shown verbatim. */
  byReason: { [code: string]: number };
  byType: { docTypeId: number; name: string | null; hidden: number }[];
}

/** A list row (same shape as `DocListEntry`) plus the access-rights facts and reason codes. */
export interface HiddenDocsRow extends Omit<DocListEntry, 'DOCACTIVE'> {
  /** `DOCACTIVE` arrives as a boolean or 0/1 depending on the driver; only ever tested for falsiness. */
  DOCACTIVE?: boolean | number;
  /** The document's own `DocAccessRights.SGMGROUPID`; `null` when it has no rights row. */
  docGroup: number | null;
  typeDefaultGroup: number | null;
  /** Reason codes; may contain codes this build does not know. */
  reasons: string[];
}

export interface HiddenDocsResponse {
  target: HiddenDocsTarget;
  window: { fromDate: string; toDate: string | null; mainCompany: number };
  summary: HiddenDocsSummary;
  rows: HiddenDocsRow[];
  truncated: boolean;
  /** Present when `target.resolvable === false`. */
  message?: string;
}

/** One `/unlisted` row: raw `Documents` columns (the joins that would supply names are the ones that failed), plus reasons. */
export interface UnlistedDocsRow {
  DOCID: number;
  DOCTYPEID: number;
  DOCDATE: Date | string;
  DOCNUMBER: string;
  DOCCLIENTSID: number | null;
  DOCPERSONID: number | null;
  DOCMANPERID: number | null;
  DOCLCOCLIID: number | null;
  DOCMANCLIID: number | null;
  DOCCURRID: number | null;
  DOCACTIVE?: boolean | number;
  /** `NO_ACCESS_ROW` / `MISSING_*` codes; may contain codes this build does not know. */
  reasons: string[];
}

export interface UnlistedDocsResponse {
  window: { fromDate: string; toDate: string | null; mainCompany: number };
  /** Reconciliation: `documentsInWindow` = listed documents (the main summary's `totalInWindow`) + `unlisted`. */
  summary: { documentsInWindow: number; unlisted: number; byReason: { [code: string]: number } };
  rows: UnlistedDocsRow[];
  truncated: boolean;
}

/** `/unlisted` is independent of any login or ladder: window, types and paging only. */
export interface UnlistedDocsQuery {
  fromDate: string;
  toDate?: string | null;
  docTypes?: number[];
  start: number;
  fetch: number;
}

export interface HiddenDocsQuery {
  login?: string;
  ladder?: number | null;
  fromDate: string;
  toDate?: string | null;
  docTypes?: number[];
  start: number;
  fetch: number;
}

/** Short English label and one-line tooltip per known reason code (single source of truth). */
export const HIDDEN_REASON_LABELS: { [code: string]: { label: string; tooltip: string } } = {
  NO_ACCESS_ROW: { label: 'No access row', tooltip: 'No access-rights row (in no list for anyone)' },
  MISSING_CLIENT: { label: 'Client row missing', tooltip: 'Client/partner row missing (in no list for anyone)' },
  MISSING_PARTNER: { label: 'Partner row missing', tooltip: 'Client/partner row missing (in no list for anyone)' },
  MISSING_CONTACT_PERSON: { label: 'Contact person missing', tooltip: 'Contact/author person row missing (in no list for anyone)' },
  MISSING_AUTHOR_PERSON: { label: 'Author person missing', tooltip: 'Contact/author person row missing (in no list for anyone)' },
  MISSING_DOCTYPE: { label: 'Document type missing', tooltip: 'Document type row missing (in no list for anyone)' },
  MISSING_LOCAL_COMPANY: { label: 'Local company missing', tooltip: 'Local company row missing (in no list for anyone)' },
  MISSING_CURRENCY: { label: 'Currency missing', tooltip: 'Currency row missing (affects some lists only)' },
  LADDER_TYPE_DEFAULT: { label: 'Type default', tooltip: "Document type's default group is above this login" },
  LADDER_RAISED: { label: 'Raised', tooltip: 'Raised individually above the type default' },
  LADDER_BELOW_TYPE_DEFAULT: { label: 'Below type default', tooltip: 'Below the type default, still above this login' },
  LADDER_TYPE_DEFAULT_UNKNOWN: { label: 'Type default unknown', tooltip: 'Group above this login; type default unknown' },
};

/** Label for a reason code; an unknown code is returned verbatim, never blank. */
export function hiddenReasonLabel(code: string): string {
  return HIDDEN_REASON_LABELS[code]?.label ?? code;
}

/** Tooltip for a reason code; an unknown code is returned verbatim. */
export function hiddenReasonTooltip(code: string): string {
  return HIDDEN_REASON_LABELS[code]?.tooltip ?? code;
}
